/**
 * Client-side JWT payload decoder.
 *
 * Used for identity-equality checks (does this token's `sub` match the
 * userId we have in localStorage?). NOT a verifier — the signature is
 * not validated. The server has already validated the token by the
 * time the frontend sees it; we just need to read the payload to
 * detect identity mismatches that would otherwise flip silently.
 *
 * Handles:
 *   - base64url alphabet (- and _ instead of + and /)
 *   - padding (Cognito tokens often omit trailing `=`)
 *   - UTF-8 (Indian-script names in claims like `name`/`given_name`)
 *
 * Returns the parsed payload object, or null if the token is malformed.
 * Callers should treat null as "can't decide" → typically fail open
 * (don't force a logout just because we can't read the payload).
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    // base64url → base64
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // restore padding
    while (b64.length % 4) b64 += '=';
    // decode to bytes, then UTF-8 → string
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Convenience: extract the `sub` claim (Cognito user UUID) from a JWT.
 * Returns null if missing or token is malformed.
 */
export function decodeJwtSub(token) {
  const payload = decodeJwtPayload(token);
  return payload?.sub || null;
}
